import React from 'react';
import { motion } from 'framer-motion';
import { calculateSolarRecommendation, formatCurrency, formatNumber } from '../../utils/solarCalculator';
import './ResultsDisplay.css';

const ResultsDisplay = ({ responses, onStartOver, onGoHome }) => {
  const { monthlyBill, electricityRate, systemType } = responses;
  
  if (!monthlyBill || !electricityRate) {
    return (
      <div className="results-display">
        <p>Please complete the questionnaire to see your results.</p>
      </div>
    );
  }

  const recommendation = calculateSolarRecommendation({
    monthlyBill,
    electricityRate: electricityRate,
    systemType: systemType || 'on-grid'
  });

  if (!recommendation) {
    return (
      <div className="results-display">
        <p>Unable to calculate recommendation. Please try again.</p>
      </div>
    );
  }

  const savingsAmount = recommendation.monthlyBill - recommendation.emi;
  const savingsPercentage = savingsAmount > 0 
    ? ((savingsAmount / recommendation.monthlyBill) * 100).toFixed(0)
    : 0;
  const isSaving = recommendation.emi <= recommendation.monthlyBill;
  const excessEMI = recommendation.emi > recommendation.monthlyBill 
    ? recommendation.emi - recommendation.monthlyBill 
    : 0;

  return (
    <div className="results-display">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="results-header"
      >
        <h2 className="results-title">Your Personalized Solar Plan is Ready! 🎉</h2>
        <p className="results-subtitle">Based on your inputs, here's what we recommend:</p>
      </motion.div>

      <div className="results-content">
        {/* System Recommendation */}
        <motion.div
          className="result-card system-recommendation"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="card-icon">⚡</div>
          <h3>Recommended System</h3>
          <div className="system-size">
            <span className="kw-value">{recommendation.kwSize}</span>
            <span className="kw-unit">KW</span>
          </div>
          <p className="card-description">
            {recommendation.systemType === 'on-grid' ? 'On-Grid' : 'Off-Grid'} Solar System
          </p>
        </motion.div>

        {/* Cost Breakdown */}
        <motion.div
          className="result-card cost-breakdown"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h3>Cost Breakdown</h3>
          <div className="cost-item">
            <span className="cost-label">Cost Before Subsidy:</span>
            <span className="cost-value">{formatCurrency(recommendation.costBeforeSubsidy)}</span>
          </div>
          <div className="cost-item highlight">
            <span className="cost-label">Government Subsidy:</span>
            <span className="cost-value subsidy">-{formatCurrency(recommendation.subsidy)}</span>
          </div>
          <div className="cost-item total">
            <span className="cost-label">Cost After Subsidy:</span>
            <span className="cost-value">{formatCurrency(recommendation.costAfterSubsidy)}</span>
          </div>
          <div className="downpayment-badge">
            <span className="badge-icon">💰</span>
            <span className="badge-text">Downpayment: Just ₹{recommendation.downpayment}!</span>
          </div>
        </motion.div>

        {/* EMI Details */}
        <motion.div
          className="result-card emi-details"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h3>EMI Details</h3>
          <div className="emi-breakdown">
            <div className="emi-item">
              <span className="emi-label">Loan Amount:</span>
              <span className="emi-value">{formatCurrency(recommendation.loanAmount)}</span>
            </div>
            <div className="emi-item">
              <span className="emi-label">Interest Rate:</span>
              <span className="emi-value">6% per annum</span>
            </div>
            <div className="emi-item">
              <span className="emi-label">Tenure:</span>
              <span className="emi-value">5 years (60 months)</span>
            </div>
            <div className="emi-item highlight">
              <span className="emi-label">Monthly EMI:</span>
              <span className="emi-value emi-amount">{formatCurrency(recommendation.emi)}</span>
            </div>
          </div>
        </motion.div>

        {/* Comparison */}
        <motion.div
          className="result-card comparison"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h3>Monthly Comparison</h3>
          <div className="comparison-grid">
            <div className="comparison-item current">
              <div className="comparison-label">Current Bill</div>
              <div className="comparison-amount">{formatCurrency(recommendation.monthlyBill)}</div>
            </div>
            <div className="comparison-arrow">→</div>
            <div className={`comparison-item emi ${isSaving ? 'saving' : 'higher'}`}>
              <div className="comparison-label">Your EMI</div>
              <div className="comparison-amount">{formatCurrency(recommendation.emi)}</div>
            </div>
          </div>
          {isSaving ? (
            <div className="savings-badge positive">
              <span className="savings-icon">💚</span>
              <span className="savings-text">
                You save ₹{formatNumber(savingsAmount)} ({savingsPercentage}%) every month! Your EMI is less than your current bill.
              </span>
            </div>
          ) : (
            <div className="savings-badge info">
              <span className="savings-icon">ℹ️</span>
              <span className="savings-text">
                <strong>Note:</strong> Your EMI is ₹{formatNumber(excessEMI)} higher than current bill. However, your solar system will generate ~{formatNumber(recommendation.monthlyGeneration.average)} units/month (worth ₹{formatNumber(recommendation.generatedValue)}), which will offset your electricity costs. After the 5-year loan period, you'll save ₹{formatNumber(recommendation.monthlyBill)}/month with zero electricity bills!
              </span>
            </div>
          )}
        </motion.div>

        {/* Generation Estimate */}
        <motion.div
          className="result-card generation"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <h3>Expected Generation</h3>
          <div className="generation-info">
            <div className="generation-range">
              <span className="generation-label">Monthly Units:</span>
              <span className="generation-value">
                {formatNumber(recommendation.monthlyGeneration.min)} - {formatNumber(recommendation.monthlyGeneration.max)} units
              </span>
            </div>
            <p className="generation-note">
              Average: ~{formatNumber(recommendation.monthlyGeneration.average)} units per month
            </p>
            {recommendation.systemType === 'on-grid' && (
              <div className="grid-info">
                <p>☀️ During the day, your solar panels will generate electricity. Excess power will be sent to the grid.</p>
                <p>🌙 At night, you'll draw electricity from the grid. Your bill will be based on net usage.</p>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      <motion.div
        className="results-actions"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <button className="btn-primary" onClick={() => window.location.href = 'tel:9430847044'}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
          </svg>
          Call Us Now: 9430847044
        </button>
        <div className="results-secondary-actions">
          <button className="btn-secondary" onClick={onStartOver}>
            Start Over
          </button>
          {onGoHome && (
            <button className="btn-tertiary" onClick={onGoHome}>
              Go to Home
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ResultsDisplay;

